/**
 * `/v1/tasks` and `/v1/departments` — every endpoint the Tasks page consumes.
 *
 * Nothing here declares a DTO of its own: `@tvitvi/shared` is the contract and this module only
 * knows the paths and the cache tier. Every response is parsed with the very schema the API
 * serialised it with, so a contract change is a typecheck failure rather than a runtime surprise.
 *
 * The query keys live in `apps/web/src/api/query-keys.ts` with every other key, which is where
 * CLAUDE.md says a cross-cutting concern belongs. They were briefly in this file because three
 * agents were building in parallel and two of them appending to one shared file is the merge this
 * repository organises itself to avoid; they moved back the moment nobody else was writing here.
 *
 * DELETE IS NOT UNDOABLE HERE, WHICH IS WHY THE PAGE ASKS FIRST. `DELETE /v1/tasks/:id` is a soft
 * delete that keeps the row's id so audit entries and notifications still resolve — but there is
 * no restore route (`task:restore` is in the matrix, waiting for one), and `POST /v1/tasks` mints
 * a new id rather than reviving the old one. An "undo" built on create would produce a different
 * task wearing the same title, and every notification and audit row already written would still
 * point at the dead one. DESIGN.md §11.4 forbids a dialog for something that CAN be undone; this
 * cannot, so it gets one.
 */
import {
  applyTaskTemplateResponseSchema,
  departmentListResponseSchema,
  taskCommentDtoSchema,
  taskCommentListResponseSchema,
  taskDtoSchema,
  taskListResponseSchema,
  taskTemplateDtoSchema,
  taskTemplateListResponseSchema,
  type ApplyTaskTemplateRequest,
  type ApplyTaskTemplateResponse,
  type CaptureTaskTemplateRequest,
  type CreateTaskRequest,
  type DepartmentListResponse,
  type TaskCommentDto,
  type TaskCommentListResponse,
  type TaskDto,
  type TaskListResponse,
  type TaskTemplateDto,
  type TaskTemplateListResponse,
  type TaskPriority,
  type TaskStatus,
  type UpdateTaskRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk, type TaskListFilters } from '@/api/query-keys';

/**
 * The list page size. `MAX_PAGE_SIZE` is 100 and a grouped view wants as much of the set as one
 * request can carry: a group header that summarises fifty of a person's eighty tasks is a header
 * that is wrong about the rest.
 */
const PAGE_SIZE = 100;

/** Departments are a short list per organization; one page is all of them. */
const DEPARTMENT_PAGE_SIZE = 100;

/** `?status=todo,blocked` — the comma form `statusListQuerySchema` accepts, so one parameter. */
function statusParam(statuses: readonly TaskStatus[] | undefined): string | undefined {
  return statuses && statuses.length > 0 ? statuses.join(',') : undefined;
}

/** `?priority=high,urgent`, read by the same preprocessor on the other side. */
function priorityParam(priorities: readonly TaskPriority[] | undefined): string | undefined {
  return priorities && priorities.length > 0 ? priorities.join(',') : undefined;
}

/**
 * Spørringen lista sender, som et objekt — den samme eksporten får som filter (docs/EKSPORT.md:
 * «eksporten følger det brukeren ser»), så de to kan ikke bli uenige om hva lista er.
 */
export function taskListQuery(filters: TaskListFilters): Record<string, string | number> {
  return {
    sort: filters.sort ?? 'due',
    direction: filters.direction ?? 'asc',
    limit: PAGE_SIZE,
    ...(filters.assigneePersonId !== undefined
      ? { assigneePersonId: filters.assigneePersonId }
      : {}),
    ...(statusParam(filters.statuses) !== undefined
      ? { status: statusParam(filters.statuses) }
      : {}),
    ...(priorityParam(filters.priorities) !== undefined
      ? { priority: priorityParam(filters.priorities) }
      : {}),
    ...(filters.departmentId !== undefined ? { departmentId: filters.departmentId } : {}),
    /*
     * «FORFALT» is sent only when it is ON. `?overdue=false` and no parameter mean the same
     * thing to the server, and a request that carries the off state is a request that says
     * something it does not mean — the same rule the address bar follows one layer up.
     */
    ...(filters.overdue === true ? { overdue: 'true' } : {}),
    ...(filters.search === undefined ? {} : { search: filters.search }),
    /*
     * THE CALENDAR'S DAY DETAIL COMES THROUGH HERE, AND THAT IS THE WHOLE DESIGN.
     *
     * `contracts/calendar.ts` is explicit: the month grid asks `/v1/calendar` for counts
     * because fifty tasks a day cannot be listed in a cell, and clicking a day then asks
     * THIS list for that one day. Two inclusive calendar days, `dueFrom === dueTo` for a
     * single day. Adding them here rather than giving the calendar a fetch function of
     * its own is what keeps the day detail from growing its own sorting, its own page
     * size and its own drift — "it is the list everybody already has".
     */
    ...(filters.dueFrom !== undefined ? { dueFrom: filters.dueFrom } : {}),
    ...(filters.dueTo !== undefined ? { dueTo: filters.dueTo } : {}),
    /*
     * THE ÅRGANG'S OWN TASK LIST COMES THROUGH HERE, AND IT IS THE SAME LIST.
     *
     * PAGES.md on the arrangement's Tasks page: "Samme oppgavemodell som globalt, filtrert
     * på arrangementet." `listTasksQuerySchema.editionId` is that filter, and routing the
     * year's page through this one function is what keeps it from growing its own sort, its
     * own page size and its own drift — exactly the argument `dueFrom`/`dueTo` above make.
     *
     * Spread, like every other optional above it: `/tasks` must put no `editionId` key on
     * the request at all, and "the organisation's list carries no year filter" is a property
     * the tests assert on the request itself.
     */
    ...(filters.editionId !== undefined ? { editionId: filters.editionId } : {}),
    /*
     * UTVALGET AV ARRANGEMENTER (#39), ved siden av `editionId` og aldri sammen med den.
     *
     * `editionId` er årgangens EGEN liste, der året er stien. Dette er avkryssingene leseren
     * har gjort på organisasjonens liste. Kontrakten avviser et kall som bærer begge med en
     * 400 framfor å velge den ene stille — se `listTasksQuerySchema`.
     *
     * SENDT SOM EN KOMMALISTE og ikke som et gjentatt parameter: det er den kortere adressen,
     * og `preprocess` i kontrakten deler den opp igjen. Samme form som `status`.
     */
    ...(filters.editionIds === undefined || filters.editionIds.length === 0
      ? {}
      : { editionIds: filters.editionIds.join(',') }),
    ...(filters.performanceId !== undefined ? { performanceId: filters.performanceId } : {}),
    /* Spillestedets arrangement og hele huset (sak 229, V4). Spredt, som resten. */
    ...(filters.venueNightId === undefined ? {} : { venueNightId: filters.venueNightId }),
    ...(filters.festivalId === undefined ? {} : { festivalId: filters.festivalId }),
    /*
     * KOI Del 1 — etikett, «Min dag» og «Jeg følger».
     *
     * Spredt som alle de andre: en nøkkel som står der med `undefined` slippes av
     * `buildUrl`, men den ER en nøkkel — og «lista bærer ikke dette filteret» er en
     * egenskap tilgangstestene leser av selve forespørselen.
     */
    ...(filters.labelId === undefined ? {} : { labelId: filters.labelId }),
    ...(filters.myDay === undefined ? {} : { myDay: filters.myDay }),
    ...(filters.watching === true ? { watching: 'true' } : {}),
    ...(filters.linkType === undefined || filters.linkId === undefined
      ? {}
      : { linkType: filters.linkType, linkId: filters.linkId }),
  };
}

/**
 * The list, ordered AND grouped by the server.
 *
 * No cursor travels with an order change: the sort is part of `queryKey`, so a different
 * grouping is a different query with its own first page, and `filters` carries no position at
 * all. A cursor records the sort spec it was minted for and `decodeCursor` refuses it under any
 * other, so a stale one would be a 400 rather than a wrong page.
 */
export function tasksQueryOptions(orgId: string, filters: TaskListFilters) {
  return queryOptions({
    queryKey: qk.taskList(orgId, filters),
    queryFn: ({ signal }): Promise<TaskListResponse> =>
      apiRequest({
        path: '/tasks',
        // Spread rather than assigned: a key present with `undefined` is dropped by `buildUrl`
        // but is still a key, and "the request carries no assignee filter" is a property this
        // page's access-control test has to be able to assert on the request itself.
        query: taskListQuery(filters),
        schema: taskListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function taskQueryOptions(orgId: string, taskId: string) {
  return queryOptions({
    queryKey: qk.task(orgId, taskId),
    queryFn: ({ signal }): Promise<TaskDto> =>
      apiRequest({ path: `/tasks/${taskId}`, schema: taskDtoSchema, signal }),
    ...CACHE.detail,
  });
}

/**
 * "Gruppe/avdeling" — data, not an enum (principle 9).
 *
 * REFERENCE TIER, not list tier. An organization's departments change a few times a year and
 * every task form and every grouped view reads them; refetching the same eight rows behind every
 * keystroke would be a round trip in front of a form field.
 */
export function departmentsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.departmentList(orgId),
    queryFn: ({ signal }): Promise<DepartmentListResponse> =>
      apiRequest({
        path: '/departments',
        query: { sort: 'order', direction: 'asc', limit: DEPARTMENT_PAGE_SIZE },
        schema: departmentListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

/*
 * EVERY WRITE INVALIDATES THE CALENDAR AS WELL, AND IT IS A SECOND PREFIX RATHER THAN A NESTED
 * KEY.
 *
 * `/v1/calendar` counts these very rows per day, and `overdueCount` moves the moment a deadline
 * is edited or a task is completed — so a month grid left open beside the Tasks page would sit
 * on a number the reader just changed. `qk.calendar(orgId)` is deliberately NOT under
 * `qk.tasks(orgId)`: the response is on its way to carrying performances and shifts too, and a
 * completed task must not be sweeping those away by prefix. Two names on one line says out loud
 * that one act invalidates two different things.
 */
export function useCreateTask(
  orgId: string,
): UseMutationResult<TaskDto, unknown, CreateTaskRequest> {
  return useMutation({
    mutationFn: (input: CreateTaskRequest) =>
      apiRequest({ path: '/tasks', method: 'POST', body: input, schema: taskDtoSchema }),
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

export interface UpdateTaskInput {
  taskId: string;
  body: UpdateTaskRequest;
}

export function useUpdateTask(orgId: string): UseMutationResult<TaskDto, unknown, UpdateTaskInput> {
  return useMutation({
    mutationFn: ({ taskId, body }: UpdateTaskInput) =>
      apiRequest({ path: `/tasks/${taskId}`, method: 'PATCH', body, schema: taskDtoSchema }),
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

export interface SetTaskStatusInput {
  taskId: string;
  status: TaskStatus;
}

/**
 * The status transition — its own endpoint because it is its own permission (`task:complete`),
 * and the one action on this page that updates optimistically.
 *
 * DESIGN.md §7 names it: "Optimistisk UI på enkle handlinger: haker av oppgave, endrer status."
 * It is safe to be optimistic here because the change is reversible in both directions — the
 * snapshot goes back on failure, and a wrong status is one more click to correct rather than
 * something lost. What is NOT optimistic is `completedAt`: the server owns that half of the
 * pair (`ck_tasks__completed_consistency`), so the cached row shows the new status and waits for
 * the real timestamp rather than inventing one that would be a few hundred milliseconds wrong.
 */
export function useSetTaskStatus(
  orgId: string,
): UseMutationResult<
  TaskDto,
  unknown,
  SetTaskStatusInput,
  { entries: [readonly unknown[], unknown][] }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, status }: SetTaskStatusInput) =>
      apiRequest({
        path: `/tasks/${taskId}/status`,
        method: 'POST',
        body: { status },
        schema: taskDtoSchema,
      }),
    onMutate: async ({ taskId, status }) => {
      await queryClient.cancelQueries({ queryKey: qk.tasks(orgId) });
      const entries = queryClient.getQueriesData({ queryKey: qk.tasks(orgId) });

      for (const [key, value] of entries) {
        const list = value as TaskListResponse | undefined;
        if (list && Array.isArray(list.items)) {
          queryClient.setQueryData(key, {
            ...list,
            items: list.items.map((row) => (row.id === taskId ? { ...row, status } : row)),
          });
          continue;
        }
        const detail = value as TaskDto | undefined;
        if (detail && detail.id === taskId) queryClient.setQueryData(key, { ...detail, status });
      }
      return { entries: entries as [readonly unknown[], unknown][] };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.entries ?? []) queryClient.setQueryData(key, value);
    },
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

export function useDeleteTask(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (taskId: string) => apiRequest({ path: `/tasks/${taskId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

/** «Angre» på en slettet oppgave (239.49): samme rad og samme id tilbake. */
export function useRestoreTask(orgId: string): UseMutationResult<TaskDto, unknown, string> {
  return useMutation({
    mutationFn: (taskId: string) =>
      apiRequest({ path: `/tasks/${taskId}/restore`, method: 'POST', schema: taskDtoSchema }),
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

/*
 * ---------------------------------------------------------------------------------------------
 * THE THREE RELATIONSHIPS A TASK CARRIES (PAGES.md, Tasks [F1]), AND WHY ONLY ONE OF THEM FETCHES.
 *
 * `coAssignees`, `blockedBy` and `blocking` are FIELDS of `TaskDto` — the API reads them in the
 * query that already found the task — so there is no query here for them at all. Their mutations
 * return the whole updated task, which the detail cache takes directly.
 *
 * Comments are a list of their own, because they grow without bound and the task does not: a page
 * that carried forty comments inline would refetch all of them every time the deadline moved.
 */

export function taskCommentsQueryOptions(orgId: string, taskId: string) {
  return queryOptions({
    queryKey: qk.taskComments(orgId, taskId),
    queryFn: ({ signal }): Promise<TaskCommentListResponse> =>
      apiRequest({
        path: `/tasks/${taskId}/comments`,
        schema: taskCommentListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export interface SetTaskCoAssigneesInput {
  taskId: string;
  personIds: readonly string[];
}

/**
 * MEDANSVARLIGE — the whole list, replaced.
 *
 * `PUT` and not `PATCH`, because two people editing the same task on a production week must not
 * get an outcome that depends on which request landed first. The contract says the rest.
 */
export function useSetTaskCoAssignees(
  orgId: string,
): UseMutationResult<TaskDto, unknown, SetTaskCoAssigneesInput> {
  return useMutation({
    mutationFn: ({ taskId, personIds }: SetTaskCoAssigneesInput) =>
      apiRequest({
        path: `/tasks/${taskId}/co-assignees`,
        method: 'PUT',
        body: { personIds: [...personIds] },
        schema: taskDtoSchema,
      }),
    // Not the calendar: who is on a task does not move a deadline or a count.
    meta: { invalidates: [qk.tasks(orgId)] },
  });
}

export interface SetTaskDependenciesInput {
  taskId: string;
  blockedByTaskIds: readonly string[];
}

/**
 * «VENTER PÅ» — likewise the whole list.
 *
 * IT INVALIDATES `qk.tasks(orgId)` AND NOT ONLY THIS TASK, because a dependency is two rows on
 * screen: adding "A waits for B" changes what B is blocking, and B's own detail page is a
 * different cache entry. The prefix covers both without this having to name the other id.
 */
export function useSetTaskDependencies(
  orgId: string,
): UseMutationResult<TaskDto, unknown, SetTaskDependenciesInput> {
  return useMutation({
    mutationFn: ({ taskId, blockedByTaskIds }: SetTaskDependenciesInput) =>
      apiRequest({
        path: `/tasks/${taskId}/dependencies`,
        method: 'PUT',
        body: { blockedByTaskIds: [...blockedByTaskIds] },
        schema: taskDtoSchema,
      }),
    meta: { invalidates: [qk.tasks(orgId)] },
  });
}

export interface AddTaskCommentInput {
  taskId: string;
  body: string;
}

export function useAddTaskComment(
  orgId: string,
): UseMutationResult<TaskCommentDto, unknown, AddTaskCommentInput> {
  return useMutation({
    mutationFn: ({ taskId, body }: AddTaskCommentInput) =>
      apiRequest({
        path: `/tasks/${taskId}/comments`,
        method: 'POST',
        body: { body },
        schema: taskCommentDtoSchema,
      }),
    meta: { invalidates: [qk.tasks(orgId)] },
  });
}

export interface RemoveTaskCommentInput {
  taskId: string;
  commentId: string;
}

export function useRemoveTaskComment(
  orgId: string,
): UseMutationResult<void, unknown, RemoveTaskCommentInput> {
  return useMutation({
    mutationFn: ({ taskId, commentId }: RemoveTaskCommentInput) =>
      apiRequest({ path: `/tasks/${taskId}/comments/${commentId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.tasks(orgId)] },
  });
}

/*
 * ---------------------------------------------------------------------------------------------
 * OPPGAVEMALER (PAGES.md, Tasks [F1]).
 *
 * REFERENCE TIER, not list tier. An organisation has a handful of templates and they change a few
 * times a year; the control that offers them opens beside a task list somebody is already reading,
 * and refetching seven rows every time it opens would be a round trip in front of a menu.
 *
 * BOTH WRITES INVALIDATE `qk.tasks(orgId)` AS WELL AS THE TEMPLATES. Applying one creates tasks —
 * that is what it is for — and capturing one reads them, which is not a write but lands the reader
 * on a list they are about to compare against what they just saved.
 */

export function taskTemplatesQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.taskTemplateList(orgId),
    queryFn: ({ signal }): Promise<TaskTemplateListResponse> =>
      apiRequest({
        path: '/task-templates',
        schema: taskTemplateListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function useCaptureTaskTemplate(
  orgId: string,
): UseMutationResult<TaskTemplateDto, unknown, CaptureTaskTemplateRequest> {
  return useMutation({
    mutationFn: (input: CaptureTaskTemplateRequest) =>
      apiRequest({
        path: '/task-templates/capture',
        method: 'POST',
        body: input,
        schema: taskTemplateDtoSchema,
      }),
    meta: { invalidates: [qk.taskTemplates(orgId)] },
  });
}

export interface ApplyTaskTemplateInput {
  templateId: string;
  body: ApplyTaskTemplateRequest;
}

export function useApplyTaskTemplate(
  orgId: string,
): UseMutationResult<ApplyTaskTemplateResponse, unknown, ApplyTaskTemplateInput> {
  return useMutation({
    mutationFn: ({ templateId, body }: ApplyTaskTemplateInput) =>
      apiRequest({
        path: `/task-templates/${templateId}/apply`,
        method: 'POST',
        body,
        schema: applyTaskTemplateResponseSchema,
      }),
    // The tasks AND the calendar: applying a template creates dated work, and a month grid open
    // beside this page would otherwise sit on counts the reader just changed.
    meta: { invalidates: [qk.tasks(orgId), qk.calendar(orgId)] },
  });
}

export function useDeleteTaskTemplate(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (templateId: string) =>
      apiRequest({ path: `/task-templates/${templateId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.taskTemplates(orgId)] },
  });
}
